import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Box } from "@mui/system";
import Checkbox from "../../../../../components/Buttons/Checkbox/Checkbox";
import CustomStyledMenu from "../../../../../components/Menu/CustomStyledMenu";
import SearchField from "../../../../../components/InputFields/SearchField";
import SkeletonLoader from "../../../../../components/Loaders/SkeletonLoader";
import TypographyInline from "../../../../../components/Typography/TypographyInline";
import IconComponent from "../../../../../components/Icon/IconComponent";
import CustomTooltip from "../../../../../components/Tooltip/CustomTooltip";
import {
  GREY,
  BORDER,
  BASE,
  ACTIONS,
  PRIMARY,
} from "../../../../../utils/constants/colors";
import { ICON_LABELS, fontWeight } from "../../../../../utils/constants/UI";
import { borderRadius } from "../../../../../utils/constants/UI/uiConstants";

// ─── Styles ─────────────────────────────────────────────────────────────
const TRIGGER = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  minHeight: "40px",
  width: "100%",
  padding: "6px 12px",
  border: `1px solid ${BORDER.LIGHT}`,
  borderRadius: borderRadius.primary,
  background: BASE.WHITE,
  cursor: "pointer",
  transition: "border-color 120ms ease, box-shadow 120ms ease",
};
const TRIGGER_FOCUSED = {
  ...TRIGGER,
  borderColor: PRIMARY.W_500,
  boxShadow: `0 0 0 3px ${PRIMARY.W_500}1A`,
};
const TRIGGER_PILLS = {
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: "6px",
  flex: 1,
  minWidth: 0,
};
const PILL = {
  display: "inline-flex",
  alignItems: "center",
  gap: "4px",
  background: ACTIONS.DEFAULT,
  color: GREY.PRIMARY,
  fontWeight: 500,
  fontSize: "12px",
  lineHeight: 1.2,
  padding: "3px 4px 3px 10px",
  borderRadius: "20px",
  maxWidth: "200px",
  border: `1px solid ${BORDER.LIGHT}`,
};
const PILL_LABEL = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  maxWidth: "160px",
};
const PILL_REMOVE = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "16px",
  height: "16px",
  borderRadius: "50%",
  cursor: "pointer",
  background: "transparent",
  border: "none",
  padding: 0,
  color: GREY.SECONDARY,
};
const PILL_OVERFLOW = {
  display: "inline-flex",
  alignItems: "center",
  background: ACTIONS.HOVER,
  color: GREY.SECONDARY,
  fontWeight: 500,
  fontSize: "11px",
  padding: "3px 10px",
  borderRadius: "20px",
};
const PLACEHOLDER = {
  color: GREY.TERTIARY,
  fontSize: "13px",
  flex: 1,
  minWidth: 0,
};
const TRIGGER_CARET = { marginLeft: "auto", color: GREY.SECONDARY };

const POPOVER = {
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
  borderRadius: borderRadius.primary,
  overflow: "hidden",
};
const PANES = { display: "flex", minWidth: 0, borderRadius: borderRadius.primary, overflow: "hidden" };
const PANE_LEFT = (expanded) => ({
  width: expanded ? "40%" : "100%",
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
  borderRight: expanded ? `1px solid ${BORDER.LIGHT}` : "none",
});
const PANE_RIGHT = {
  width: "60%",
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
  background: ACTIONS.HOVER,
};
const HEADER_BAND = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "8px 12px",
  borderBottom: `1px solid ${BORDER.LIGHT}`,
  background: BASE.WHITE,
  gap: "8px",
  minHeight: "36px",
};
const SEARCH_WRAP = {
  padding: "8px 10px",
  borderBottom: `1px solid ${BORDER.LIGHT}`,
  background: BASE.WHITE,
};
// Show up to 4 rows (44px each); scroll for the rest. No min-height so 1-3
// rows don't leave dead whitespace at the bottom.
const LIST = {
  maxHeight: "176px", // 4 × 44px
  overflowY: "auto",
};
const GROUP_ROW = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  padding: "10px 12px",
  borderTop: `1px solid ${BORDER.LIGHT}`,
  minHeight: "44px",
  cursor: "pointer",
  margin: 0,
  background: BASE.WHITE,
  transition: "background 120ms ease",
};
const GROUP_ROW_HOVER = {
  ...GROUP_ROW,
  background: ACTIONS.HOVER,
};
const GROUP_ROW_ACTIVE = {
  ...GROUP_ROW,
  background: ACTIONS.HOVER,
  boxShadow: `inset 2px 0px 0px 0px ${PRIMARY.W_500}`,
};
const MEMBER_ROW = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  padding: "10px 12px",
  borderTop: `1px solid ${BORDER.LIGHT}`,
  minHeight: "44px",
  cursor: "pointer",
  margin: 0,
  background: "transparent",
  transition: "background 120ms ease",
};
const COUNT_CHIP = {
  display: "inline-flex",
  alignItems: "center",
  background: ACTIONS.HOVER,
  color: GREY.SECONDARY,
  fontSize: "10px",
  fontWeight: 600,
  padding: "1px 6px",
  borderRadius: "10px",
  marginLeft: "6px",
};
const MEMBER_SIG = {
  marginLeft: "auto",
  fontSize: "11px",
  color: GREY.TERTIARY,
  maxWidth: "55%",
  textAlign: "right",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};
const VIEW_MORE_BTN = {
  display: "inline-flex",
  alignItems: "center",
  gap: "2px",
  cursor: "pointer",
  padding: "4px 8px",
  borderRadius: borderRadius.primary,
};
const HEADER_TITLE = {
  fontSize: "11px",
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: GREY.SECONDARY,
  fontWeight: 600,
};
const HEADER_ACTIONS = {
  display: "inline-flex",
  alignItems: "center",
  gap: "2px",
};
const HEADER_ICON_BTN = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "28px",
  height: "28px",
  borderRadius: "6px",
  border: "none",
  background: "transparent",
  cursor: "pointer",
  padding: 0,
  color: GREY.SECONDARY,
};
const FOOTER_LINK = {
  cursor: "pointer",
  fontSize: "12px",
  color: PRIMARY.W_500,
  fontWeight: 500,
};
const VISIBLE_PILLS = 3;

// ─── Component ──────────────────────────────────────────────────────────
const GroupSenderPicker = ({
  // "group" -> mailbox groups only (group APIs). "standalone" -> individual
  // connected mailboxes only (individual-mailbox API). The radio in the parent
  // owns this; the picker never auto-falls-back from one to the other.
  mode = "group",
  groups = [],
  groupsLoading = false,
  selectedGroupIds = [],
  membersById = {},
  membersLoadingById = {},
  excludedEmailsByGroupId = {},
  onToggleGroup = () => {},
  onExpandGroup = () => {},
  onToggleMember = () => {},
  onOpenSettings = () => {},
  // Standalone mode: plain individual mailboxes inside the same popover.
  availableEmails = [],
  espIconByEsp = {},
  // GMAIL SEND-AS ALIAS: account email -> chosen alias, DISPLAY ONLY. Every
  // checkbox/selection/payload still uses the account email; a mailbox with no
  // alias is absent from this map and renders exactly as before.
  aliasByEmail = {},
  // No Send Via provider selected at all - both panes show a "select a
  // provider" prompt instead of their normal empty-state message, since an
  // empty availableEmails/groups list here doesn't mean "genuinely none
  // connected", it means we haven't been told which provider to check yet.
  noProviderSelected = false,
  selectedSenderEmails = [],
  onToggleSenderEmail = () => {},
  signaturesByEmail = {},
  signatureListByEmail = {},
  signatureLoadingByEmail = {},
}) => {
  // Render source is driven strictly by the radio mode, never by group count.
  const isStandalone = mode === "standalone";
  const hasGroups = !isStandalone;
  const triggerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [activeGroupId, setActiveGroupId] = useState(null);
  const [groupQuery, setGroupQuery] = useState("");
  const [memberQuery, setMemberQuery] = useState("");
  const [triggerWidth, setTriggerWidth] = useState(0);

  const expanded = !!activeGroupId;

  // Measure the trigger so the popover matches its width by default; when
  // expanded for View more, grow up to a useful two-pane size.
  useLayoutEffect(() => {
    if (triggerRef.current) {
      setTriggerWidth(triggerRef.current.getBoundingClientRect().width);
    }
  }, [open]);
  useEffect(() => {
    const onResize = () => {
      if (triggerRef.current) {
        setTriggerWidth(triggerRef.current.getBoundingClientRect().width);
      }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Group members are used AS-IS. Group mode doesn't fetch the individual
  // mailbox list, so there is no provider intersection to apply here.
  const visibleMembers = (gid) => membersById[gid] || [];

  const groupCheckState = (gid) => {
    if (!selectedGroupIds.includes(gid))
      return { checked: false, partial: false };
    const visible = visibleMembers(gid);
    if (!visible.length) return { checked: true, partial: false };
    const excluded = excludedEmailsByGroupId[gid] || new Set();
    const excludedCount = visible.filter((m) => excluded.has(m.email)).length;
    if (excludedCount === 0) return { checked: true, partial: false };
    if (excludedCount === visible.length)
      return { checked: false, partial: true };
    return { checked: true, partial: true };
  };

  const selectedGroupObjs = useMemo(
    () => groups.filter((g) => selectedGroupIds.includes(g._id)),
    [groups, selectedGroupIds],
  );

  const totalSendingCount = useMemo(() => {
    const set = new Set();
    selectedGroupIds.forEach((gid) => {
      const excluded = excludedEmailsByGroupId[gid] || new Set();
      visibleMembers(gid).forEach((m) => {
        if (!excluded.has(m.email)) set.add(m.email);
      });
    });
    return set.size;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedGroupIds.join("|"),
    Object.keys(membersById).join("|"),
    Object.keys(excludedEmailsByGroupId)
      .map(
        (k) =>
          `${k}:${[...(excludedEmailsByGroupId[k] || new Set())].join(",")}`,
      )
      .join("|"),
  ]);

  // Pill data: groups in group mode, selected mailbox emails in standalone.
  const pillEntries = hasGroups
    ? selectedGroupObjs.map((g) => ({
        id: g._id,
        label: g.group_name,
        onRemove: () => {
          onToggleGroup(g._id);
          if (activeGroupId === g._id) setActiveGroupId(null);
        },
      }))
    : selectedSenderEmails.map((email) => ({
        id: email,
        // pill shows the alias when one is chosen; id/removal stay on the account
        label: aliasByEmail[email] || email,
        onRemove: () => onToggleSenderEmail(email),
      }));
  const pillsVisible = pillEntries.slice(0, VISIBLE_PILLS);
  const pillsOverflow = pillEntries.length - pillsVisible.length;
  const triggerCountLabel = hasGroups
    ? `${totalSendingCount} sending`
    : `${selectedSenderEmails.length} selected`;
  const triggerPlaceholder = hasGroups
    ? "Pick groups to send from"
    : "Pick mailboxes to send from";

  const filteredGroups = useMemo(() => {
    const q = groupQuery.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter((g) =>
      (g.group_name || "").toLowerCase().includes(q),
    );
  }, [groups, groupQuery]);

  // No-groups mode: filter the flat mailbox pool against the same query.
  // Matches the ACCOUNT email and the alias, so a user can find the row by
  // whichever address they remember.
  const filteredMailboxes = useMemo(() => {
    const q = groupQuery.trim().toLowerCase();
    if (!q) return availableEmails;
    return availableEmails.filter(
      (m) =>
        (m.label || "").toLowerCase().includes(q) ||
        (m.alias || "").toLowerCase().includes(q),
    );
  }, [availableEmails, groupQuery]);

  const activeGroup = useMemo(
    () => groups.find((g) => g._id === activeGroupId) || null,
    [groups, activeGroupId],
  );
  const filteredMembers = useMemo(() => {
    if (!activeGroupId) return [];
    const all = visibleMembers(activeGroupId);
    const q = memberQuery.trim().toLowerCase();
    if (!q) return all;
    return all.filter((m) => (m.email || "").toLowerCase().includes(q));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGroupId, membersById, memberQuery]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setActiveGroupId(null);
    setGroupQuery("");
    setMemberQuery("");
  };
  const handleViewMore = (e, gid) => {
    e.preventDefault();
    e.stopPropagation();
    onExpandGroup(gid);
    setActiveGroupId(gid === activeGroupId ? null : gid);
    setMemberQuery("");
  };
  const handlePillRemove = (e, gid) => {
    e.stopPropagation();
    onToggleGroup(gid);
    if (activeGroupId === gid) setActiveGroupId(null);
  };

  // Collapsed: match the trigger width so it sits flush.
  // Expanded: grow into a wider two-pane surface, capped by viewport so it
  // never clips on smaller laptops (Mac mini / 13" etc.).
  const popoverWidth = (() => {
    if (!expanded) {
      return triggerWidth ? `${triggerWidth}px` : "380px";
    }
    const viewportCap =
      typeof window !== "undefined"
        ? Math.min(900, window.innerWidth - 64)
        : 720;
    const desired = Math.max(triggerWidth + 240, 640);
    return `${Math.min(desired, viewportCap)}px`;
  })();

  return (
    <Box>
      <Box
        ref={triggerRef}
        style={open ? TRIGGER_FOCUSED : TRIGGER}
        onClick={handleOpen}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleOpen();
          }
        }}
      >
        <Box style={TRIGGER_PILLS}>
          {pillsVisible.length === 0 ? (
            <span style={PLACEHOLDER}>{triggerPlaceholder}</span>
          ) : (
            <>
              {pillsVisible.map((p) => (
                <span key={p.id} style={PILL} title={p.label}>
                  <span style={PILL_LABEL}>{p.label}</span>
                  <button
                    type="button"
                    style={PILL_REMOVE}
                    onClick={(e) => {
                      e.stopPropagation();
                      p.onRemove();
                    }}
                    aria-label={`Remove ${p.label}`}
                  >
                    <IconComponent
                      iconLabel={ICON_LABELS.CLOSE}
                      fontSize="12px"
                      color="inherit"
                    />
                  </button>
                </span>
              ))}
              {pillsOverflow > 0 && (
                <span style={PILL_OVERFLOW}>{`+${pillsOverflow}`}</span>
              )}
              <span
                style={{
                  fontSize: "11px",
                  color: GREY.TERTIARY,
                  marginLeft: "4px",
                }}
              >
                · {triggerCountLabel}
              </span>
            </>
          )}
        </Box>
        <IconComponent
          iconLabel={
            open
              ? ICON_LABELS.KEYBOARD_ARROW_UP || ICON_LABELS.EXPAND_MORE
              : ICON_LABELS.KEYBOARD_ARROW_DOWN
          }
          fontSize="18px"
          color={GREY.SECONDARY}
          style={TRIGGER_CARET}
        />
      </Box>

      <CustomStyledMenu
        anchorEl={triggerRef.current}
        open={open}
        onClose={handleClose}
        width={popoverWidth}
        noMargin
        marginTop="6px !important"
        isRightAlign
      >
        <Box style={POPOVER}>
          <Box style={PANES}>
            {/* ── Left pane: groups list ──────────────────────────── */}
            <Box style={PANE_LEFT(expanded)}>
              <Box style={HEADER_BAND}>
                <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: "2px" }}>
                  <span style={HEADER_TITLE}>
                    {hasGroups
                      ? selectedGroupIds.length > 0
                        ? `${selectedGroupIds.length} selected · ${totalSendingCount} sending`
                        : "Pick groups to send from"
                      : selectedSenderEmails.length > 0
                      ? `${selectedSenderEmails.length} selected`
                      : "Pick mailboxes to send from"}
                  </span>
                </Box>
                <Box style={HEADER_ACTIONS}>
                  <CustomTooltip
                    title="Manage groups in settings"
                    variant="top"
                  >
                    <button
                      type="button"
                      style={HEADER_ICON_BTN}
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpenSettings();
                      }}
                      aria-label="Manage groups in settings"
                    >
                      <IconComponent
                        iconLabel={ICON_LABELS.SETTINGS}
                        fontSize="16px"
                        color="inherit"
                      />
                    </button>
                  </CustomTooltip>
                  <CustomTooltip title="Close" variant="top">
                    <button
                      type="button"
                      style={HEADER_ICON_BTN}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClose();
                      }}
                      aria-label="Close"
                    >
                      <IconComponent
                        iconLabel={ICON_LABELS.CLOSE}
                        fontSize="16px"
                        color="inherit"
                      />
                    </button>
                  </CustomTooltip>
                </Box>
              </Box>
              <Box style={SEARCH_WRAP}>
                <SearchField
                  size="sm36"
                  autoComplete="off"
                  placeholder={
                    hasGroups ? "Search groups" : "Search mailboxes"
                  }
                  value={groupQuery}
                  onChange={(e) => setGroupQuery(e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()}
                />
              </Box>
              <Box style={LIST}>
                {!hasGroups ? (
                  noProviderSelected ? (
                    <Box sx={{ padding: "20px 14px", textAlign: "center" }}>
                      <TypographyInline
                        label="Select a provider first."
                        size="xs"
                        color={GREY.TERTIARY}
                        fontWeight={fontWeight.REGULAR}
                      />
                    </Box>
                  ) : filteredMailboxes.length === 0 ? (
                    <Box sx={{ padding: "20px 14px", textAlign: "center" }}>
                      <TypographyInline
                        label={
                          availableEmails.length === 0
                            ? "No connected mailboxes available."
                            : "No mailboxes match your search."
                        }
                        size="xs"
                        color={GREY.TERTIARY}
                        fontWeight={fontWeight.REGULAR}
                      />
                    </Box>
                  ) : (
                    filteredMailboxes.map((m) => {
                      const email = m.label;
                      // shown address = the alias when one is chosen (what the
                      // recipient will see); the VALUE stays the account email
                      const displayEmail = m.alias || email;
                      const isChecked = selectedSenderEmails.includes(email);
                      const sig = signaturesByEmail?.[email];
                      const isLoading = signatureLoadingByEmail?.[email];
                      const list = signatureListByEmail?.[email];
                      const sigLabel = sig?.label
                        ? sig.label
                        : isLoading
                        ? "Loading…"
                        : Array.isArray(list) && list.length === 0
                        ? "No signature"
                        : "";
                      const providerIcon = espIconByEsp?.[m.esp];
                      return (
                        <div
                          key={m.id || email}
                          style={GROUP_ROW}
                          onClick={() => onToggleSenderEmail(email, m.esp)}
                        >
                          <span onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={isChecked}
                              onChange={() => onToggleSenderEmail(email, m.esp)}
                              noMargin
                            />
                          </span>
                          {providerIcon && (
                            <img
                              src={providerIcon}
                              alt=""
                              style={{ width: "40px", height: "40px", flexShrink: 0 }}
                            />
                          )}
                          <Box
                            sx={{ flex: 1, minWidth: 0 }}
                            title={m.alias ? `Mailbox: ${email}` : undefined}
                          >
                            <TypographyInline
                              label={displayEmail}
                              size="sm"
                              color={GREY.PRIMARY}
                              fontWeight={fontWeight.MEDIUM}
                              isEllipses
                            />
                          </Box>
                          {sigLabel && (
                            <span
                              style={{
                                fontSize: "11px",
                                color: GREY.TERTIARY,
                                maxWidth: "55%",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                textAlign: "right",
                              }}
                              title={sigLabel}
                            >
                              {sigLabel}
                            </span>
                          )}
                        </div>
                      );
                    })
                  )
                ) : noProviderSelected ? (
                  <Box sx={{ padding: "20px 14px", textAlign: "center" }}>
                    <TypographyInline
                      label="Select a provider first."
                      size="xs"
                      color={GREY.TERTIARY}
                      fontWeight={fontWeight.REGULAR}
                    />
                  </Box>
                ) : groupsLoading ? (
                  <Box sx={{ padding: "12px 14px" }}>
                    <SkeletonLoader
                      type="rounded"
                      skeletonWidth={"60%"}
                      skeletonHeight={14}
                    />
                  </Box>
                ) : filteredGroups.length === 0 ? (
                  <Box
                    sx={{
                      padding: "20px 14px",
                      textAlign: "center",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <TypographyInline
                      label={
                        groups.length === 0
                          ? "No groups created yet."
                          : "No groups match your search."
                      }
                      size="xs"
                      color={GREY.TERTIARY}
                      fontWeight={fontWeight.REGULAR}
                    />
                    {groups.length === 0 && (
                      <span
                        style={FOOTER_LINK}
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenSettings();
                        }}
                      >
                        Create a group in settings
                      </span>
                    )}
                  </Box>
                ) : (
                  filteredGroups.map((g) => {
                    const isActive = activeGroupId === g._id;
                    const checkState = groupCheckState(g._id);
                    const memberCount =
                      typeof g.member_count === "number"
                        ? g.member_count
                        : (membersById[g._id] || []).length;
                    const effectiveCount = selectedGroupIds.includes(g._id)
                      ? visibleMembers(g._id).filter(
                          (m) =>
                            !(
                              excludedEmailsByGroupId[g._id] || new Set()
                            ).has(m.email),
                        ).length
                      : null;
                    return (
                      <div
                        key={g._id}
                        style={isActive ? GROUP_ROW_ACTIVE : GROUP_ROW}
                        onClick={() => onToggleGroup(g._id)}
                      >
                        <span onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={checkState.checked}
                            partial={checkState.partial}
                            onChange={() => onToggleGroup(g._id)}
                            noMargin
                          />
                        </span>
                        <Box
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            overflow: "hidden",
                          }}
                        >
                          <Box sx={{ minWidth: 0, flex: "0 1 auto" }}>
                            <TypographyInline
                              label={g.group_name}
                              size="sm"
                              color={GREY.PRIMARY}
                              fontWeight={fontWeight.MEDIUM}
                              isEllipses
                            />
                          </Box>
                          <span style={COUNT_CHIP}>
                            {effectiveCount != null
                              ? `${effectiveCount}/${memberCount}`
                              : memberCount}
                          </span>
                        </Box>
                        <span
                          style={VIEW_MORE_BTN}
                          onClick={(e) => handleViewMore(e, g._id)}
                        >
                          <TypographyInline
                            label={isActive ? "Hide" : "View more"}
                            size="xs"
                            color={PRIMARY.W_500}
                            fontWeight={fontWeight.MEDIUM}
                          />
                          <IconComponent
                            iconLabel={ICON_LABELS.KEYBOARD_ARROW_RIGHT}
                            fontSize="14px"
                            color={PRIMARY.W_500}
                          />
                        </span>
                      </div>
                    );
                  })
                )}
              </Box>
            </Box>

            {/* ── Right pane: members of active group ─────────────── */}
            {expanded && (
              <Box style={PANE_RIGHT}>
                <Box style={HEADER_BAND}>
                  <Box
                    sx={{
                      minWidth: 0,
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      overflow: "hidden",
                    }}
                  >
                    <Box sx={{ minWidth: 0, flex: "0 1 auto" }}>
                      <TypographyInline
                        label={activeGroup?.group_name || ""}
                        size="sm"
                        color={GREY.PRIMARY}
                        fontWeight={fontWeight.SEMIBOLD}
                        isEllipses
                      />
                    </Box>
                    <span style={COUNT_CHIP}>{filteredMembers.length}</span>
                  </Box>
                  <span
                    style={FOOTER_LINK}
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveGroupId(null);
                    }}
                  >
                    Close
                  </span>
                </Box>
                <Box style={SEARCH_WRAP}>
                  <SearchField
                    size="sm36"
                    autoComplete="off"
                    placeholder="Search mailboxes"
                    value={memberQuery}
                    onChange={(e) => setMemberQuery(e.target.value)}
                    onKeyDown={(e) => e.stopPropagation()}
                  />
                </Box>
                <Box style={LIST}>
                  {membersLoadingById[activeGroupId] ? (
                    <Box sx={{ padding: "12px 14px" }}>
                      <SkeletonLoader
                        type="rounded"
                        skeletonWidth={"60%"}
                        skeletonHeight={14}
                      />
                    </Box>
                  ) : filteredMembers.length === 0 ? (
                    <Box sx={{ padding: "20px 14px", textAlign: "center" }}>
                      <TypographyInline
                        label={
                          memberQuery
                            ? "No mailboxes match your search."
                            : "This group has no mailboxes."
                        }
                        size="xs"
                        color={GREY.TERTIARY}
                        fontWeight={fontWeight.REGULAR}
                      />
                    </Box>
                  ) : (
                    filteredMembers.map((m) => {
                      const isGroupOn = selectedGroupIds.includes(
                        activeGroupId,
                      );
                      const excluded =
                        excludedEmailsByGroupId[activeGroupId] || new Set();
                      const memberChecked = isGroupOn && !excluded.has(m.email);
                      const sigName =
                        m.default_signature?.signature_name ||
                        m.default_signature?.name ||
                        "";
                      return (
                        <div
                          key={m.account_id || m.email}
                          style={MEMBER_ROW}
                          onClick={() => {
                            if (!isGroupOn) return;
                            onToggleMember(activeGroupId, m.email);
                          }}
                        >
                          <span onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={memberChecked}
                              disabled={!isGroupOn}
                              onChange={() =>
                                onToggleMember(activeGroupId, m.email)
                              }
                              noMargin
                            />
                          </span>
                          <Box
                            sx={{ flex: 1, minWidth: 0 }}
                            title={aliasByEmail[m.email] ? `Mailbox: ${m.email}` : undefined}
                          >
                            <TypographyInline
                              label={aliasByEmail[m.email] || m.email}
                              size="sm"
                              color={GREY.PRIMARY}
                              fontWeight={fontWeight.REGULAR}
                              isEllipses
                            />
                          </Box>
                          <span
                            style={MEMBER_SIG}
                            title={sigName || "No signature"}
                          >
                            {sigName || "No signature"}
                          </span>
                        </div>
                      );
                    })
                  )}
                </Box>
              </Box>
            )}
          </Box>

        </Box>
      </CustomStyledMenu>
    </Box>
  );
};

export default GroupSenderPicker;
